import { useRouter } from "@tanstack/react-router"
import { useEffect, useState } from "react"

import {
  ModeSelect,
  OptionSelect,
  usePortfolios,
} from "@/admin/companies/portfolio-controls"
import {
  addPortfolioEntry,
  loadPortfolioEntries,
  offboardPortfolioEntry,
  type PortfolioEntryRow,
  refreshSnapshot,
  setPortfolioEntryMode,
} from "@/admin/companies/portfolio-entries-data"
import { terminalControlVariants } from "@/components/terminal/control-variants"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { errorMessage } from "@/lib/error-message"
import { formatTerminalDate } from "@/lib/terminal/date"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"

// The Portfolios that hold one Company, and the console's edits to them.
export const PortfolioEntriesSheet = ({
  company,
  onClose,
}: {
  company: { companyId: string; displayName: string } | null
  onClose: () => void
}) => {
  const { theme } = useTerminalTheme()
  const router = useRouter()
  const portfolios = usePortfolios(company !== null)
  const [entries, setEntries] = useState<PortfolioEntryRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [added, setAdded] = useState({ portfolioId: "", isMonitoring: true })
  const companyId = company?.companyId

  const load = (id: string) =>
    loadPortfolioEntries({ data: { companyId: id } }).then(setEntries)

  useEffect(() => {
    setEntries(null)
    setError(null)
    if (companyId)
      load(companyId).catch((cause: unknown) => setError(errorMessage(cause)))
  }, [companyId])

  // Each edit reloads the panel and the board behind it.
  const save = (edit: () => Promise<unknown>) => {
    if (!companyId) return
    setIsSaving(true)
    setError(null)
    edit()
      .then(() => Promise.all([load(companyId), router.invalidate()]))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setIsSaving(false))
  }

  const heldPortfolioIds = new Set(entries?.map((entry) => entry.portfolioId))
  const addable = portfolios.filter(({ id }) => !heldPortfolioIds.has(id))

  return (
    <Sheet
      open={company !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
    >
      <SheetContent
        data-terminal-theme={theme}
        className="bg-surface-layer text-text-primary w-full gap-0 sm:max-w-xl"
      >
        <SheetHeader className="shrink-0 border-b pr-12">
          <SheetTitle>{company?.displayName}</SheetTitle>
          <SheetDescription className="text-xs">
            The Portfolios that hold this Company. A monitored entry follows
            every new File and shows on the customer's Monitoring list; a
            snapshot pins one File and shows on One-time.
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4 text-xs">
          {error && (
            <p role="alert" className="text-assessment-critical">
              {error}
            </p>
          )}
          {!entries && !error && (
            <p role="status" className="text-muted-foreground">
              Loading Portfolios…
            </p>
          )}
          {entries?.length === 0 && (
            <p className="text-muted-foreground">No Portfolio holds it.</p>
          )}
          {entries?.map((entry) => {
            const portfolioEntryId = entry.id
            return (
              <section key={entry.id} className="space-y-2 border-b pb-4">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-sm font-semibold">
                    {entry.portfolioName}
                  </h3>
                  {!entry.isMonitoring && (
                    <span className="text-muted-foreground">
                      {entry.snapshotAt
                        ? `Pinned ${formatTerminalDate(entry.snapshotAt)}`
                        : "Waiting for a Brief"}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <ModeSelect
                    isMonitoring={entry.isMonitoring}
                    isDisabled={isSaving}
                    onChange={(isMonitoring) =>
                      save(() =>
                        setPortfolioEntryMode({
                          data: { portfolioEntryId, isMonitoring },
                        })
                      )
                    }
                  />
                  {!entry.isMonitoring && (
                    <button
                      type="button"
                      className={terminalControlVariants({ size: "m" })}
                      disabled={isSaving}
                      onClick={() =>
                        save(() =>
                          refreshSnapshot({ data: { portfolioEntryId } })
                        )
                      }
                    >
                      Refresh snapshot
                    </button>
                  )}
                  <button
                    type="button"
                    className={terminalControlVariants({ size: "m" })}
                    disabled={isSaving}
                    onClick={() =>
                      save(() =>
                        offboardPortfolioEntry({ data: { portfolioEntryId } })
                      )
                    }
                  >
                    Offboard
                  </button>
                </div>
              </section>
            )
          })}
          {entries && addable.length > 0 && (
            <form
              className="space-y-2"
              onSubmit={(event) => {
                event.preventDefault()
                if (!companyId) return
                save(async () => {
                  await addPortfolioEntry({
                    data: {
                      portfolioId: added.portfolioId,
                      companyId,
                      isMonitoring: added.isMonitoring,
                    },
                  })
                  setAdded({ ...added, portfolioId: "" })
                })
              }}
            >
              <h3 className="text-sm font-semibold">Add to a Portfolio</h3>
              <div className="flex flex-wrap items-center gap-2">
                <OptionSelect
                  label="Portfolio"
                  value={added.portfolioId}
                  options={addable.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))}
                  onChange={(portfolioId) =>
                    setAdded({ ...added, portfolioId })
                  }
                />
                <ModeSelect
                  isMonitoring={added.isMonitoring}
                  onChange={(isMonitoring) =>
                    setAdded({ ...added, isMonitoring })
                  }
                />
                <button
                  type="submit"
                  className={terminalControlVariants({
                    size: "m",
                    intent: "primary",
                  })}
                  disabled={isSaving || !added.portfolioId}
                >
                  Add
                </button>
              </div>
            </form>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
